import { AccountBundle } from '../flowMapTypes';
import { baseUrlPolicy, BaseUrlPolicy } from '../baseUrlPolicy';
import { validateBaseUrl } from 'connections/validateBaseUrl';
import {
  constructEarthRangerUrl,
  extractEarthRangerSiteName,
  isEarthRangerConnectionType,
} from 'connections/earthRangerUrl';

type Translate = (key: any, options?: any) => string;

export type AccountBaseUrl = BaseUrlPolicy & {
  // Edited as an EarthRanger site name rather than a full URL.
  asSiteName: boolean;
  // What the field shows: the site name, or the stored URL.
  value: string;
};

// Draft of an account's General fields (name / workspace / endpoint); null
// means "not edited".
export type GeneralDraft = {
  name?: string;
  owner?: { id: string; name: string };
  base_url?: string;
};

// Everything the General pane needs to know about an account's address.
// EarthRanger accounts edit a site name rather than a URL (<site>.pamdas.org);
// a site whose stored URL isn't a pamdas one is edited as a plain URL —
// rewriting it through the site input would silently move it to another host.
export const accountBaseUrl = (
  bundle: AccountBundle,
  fallbackTechValue?: string,
): AccountBaseUrl => {
  const policy = baseUrlPolicy({
    isProvider: bundle.isProvider,
    techName: bundle.techName,
    techValue: bundle.techValue || fallbackTechValue,
    actions: bundle.actions,
  });
  const erSiteName = isEarthRangerConnectionType(bundle.techName)
    ? extractEarthRangerSiteName(bundle.base_url || '')
    : '';
  const asSiteName =
    isEarthRangerConnectionType(bundle.techName) &&
    (!bundle.base_url || !!erSiteName);
  return {
    ...policy,
    asSiteName,
    value: asSiteName ? erSiteName : (bundle.base_url ?? ''),
  };
};

export type GeneralFields = {
  name: string;
  owner?: { id: string; name: string };
  url: string;
  nameError?: string;
  ownerError?: string;
  urlError?: string;
  dirty: boolean;
};

// The General pane's values: the draft where the user typed, the stored
// record everywhere else, field by field.
export const generalFields = (
  draft: GeneralDraft | null,
  bundle: AccountBundle | undefined,
  baseUrl: AccountBaseUrl | undefined,
  t: Translate,
): GeneralFields => {
  const name = draft?.name ?? bundle?.name ?? '';
  const owner = draft?.owner ?? bundle?.owner;
  const url = draft?.base_url ?? baseUrl?.value ?? '';
  const nameError =
    draft !== null && name.trim().length === 0
      ? t('Name is required.')
      : undefined;
  // Same message the Route's General pane gives.
  const ownerError =
    draft !== null && !owner?.id ? t('Select a workspace.') : undefined;
  // Same shared rule every other address field uses.
  const urlError = !baseUrl?.show
    ? undefined
    : validateBaseUrl({
        value: url,
        asSiteName: baseUrl.asSiteName,
        optional: baseUrl.optional,
        t,
      });
  const dirty =
    draft !== null &&
    !!bundle &&
    (name.trim() !== (bundle.name ?? '') ||
      (!!owner?.id && owner.id !== bundle.owner?.id) ||
      url.trim() !== (baseUrl?.value ?? ''));
  return { name, owner, url, nameError, ownerError, urlError, dirty };
};

// Only the fields that changed, in the shape the integration PATCH expects.
export const generalPatch = (
  fields: GeneralFields,
  bundle: AccountBundle,
  baseUrl: AccountBaseUrl,
) => {
  const patch: { name?: string; owner?: string; base_url?: string } = {};
  if (fields.name.trim() !== (bundle.name ?? ''))
    patch.name = fields.name.trim();
  if (fields.owner?.id && fields.owner.id !== bundle.owner?.id)
    patch.owner = fields.owner.id;
  if (fields.url.trim() !== (baseUrl.value ?? '')) {
    patch.base_url = baseUrl.asSiteName
      ? constructEarthRangerUrl(fields.url.trim())
      : fields.url.trim();
  }
  return patch;
};
